import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import PropertyCardSkeleton from "./PropertyCardSkeleton";

interface InfiniteScrollLoaderProps {
  isLoading: boolean;
  hasMore: boolean;
  isMobile?: boolean;
  loadMoreRef: (node: HTMLDivElement | null) => void;
}

export function InfiniteScrollLoader({
  isLoading,
  hasMore,
  isMobile = false,
  loadMoreRef,
}: InfiniteScrollLoaderProps) {
  const { t } = useTranslation();
  if (!hasMore) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-muted-foreground">
          {typeof window !== "undefined" && window.localStorage.getItem("i18nextLng")?.startsWith("en") ? "No more properties to show" : "Não há mais imóveis para mostrar"}
        </p>
      </div>
    );
  }

  return (
    <div ref={loadMoreRef} className="py-1">
      {isLoading ? (
        <div className="space-y-4">
          {/* Loading Skeletons */}
          <div
            className={cn(
              "grid gap-3",
              isMobile
                ? "grid-cols-2"
                : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
            )}
          >
            {Array.from({ length: isMobile ? 4 : 5 }).map((_, i) => (
              <PropertyCardSkeleton key={`infinite-${i}`} />
            ))}
          </div>

          {/* Loading Indicator */}
          <div className="flex items-center justify-center gap-2 py-4">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">
              {typeof window !== "undefined" && window.localStorage.getItem("i18nextLng")?.startsWith("en") ? "Loading more properties..." : "A carregar mais imóveis..."}
            </span>
          </div>
        </div>
      ) : (
        /* Sentinel for intersection observer */
        <div className="h-4" />
      )}
    </div>
  );
}

// Compact version for mobile bottom bar
export function InfiniteScrollIndicator({
  isLoading,
  hasMore,
}: {
  isLoading: boolean;
  hasMore: boolean;
}) {
  if (!hasMore || !isLoading) return null;

  return (
    <div className="flex items-center justify-center gap-2 py-2 bg-background/95 backdrop-blur-sm border-t">
      <Loader2 className="w-4 h-4 animate-spin text-primary" />
      <span className="text-xs text-muted-foreground">{typeof window !== "undefined" && window.localStorage.getItem("i18nextLng")?.startsWith("en") ? "Loading..." : "A carregar..."}</span>
    </div>
  );
}

export default InfiniteScrollLoader;
